import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Autocomplete,
  IconButton,
  Switch,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import {
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetCategoriesQuery,
  useGetProductsQuery,
  useGetWarrantyPeriodsQuery,
} from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ProductFormContent({ compact = false, onSuccess, onCancel, productId = null }) {
  const isEdit = !!productId;

  const { data: product, isLoading: loadingProduct } = useGetProductQuery(productId, {
    skip: !isEdit,
  });

  const [categorySearch, setCategorySearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(categorySearch), 500);
    return () => clearTimeout(handler);
  }, [categorySearch]);

  const { data: categoriesData } = useGetCategoriesQuery({ search: debouncedSearch });
  const { data: warrantyPeriodsData } = useGetWarrantyPeriodsQuery();
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const showWholesalePrice = company?.showWholesalePrice !== false;
  const enableServiceModule = company?.enableServiceModule === true;

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    barcodes: [],
    categoryId: '',
    brand: '',
    baseUnit: 'pcs',
    costPrice: '',
    wholesalePrice: '',
    sellingPrice: '',
    openingStock: '',
    maxDiscountType: '',
    maxDiscountPercent: '',
    maxDiscountAmount: '',
    isActive: true,
    type: 'product',
    hasWarranty: false,
    warrantyPeriodId: '',
    requiresDualImei: false,
    isBundle: false,
  });
  const [bundleItems, setBundleItems] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');

  const [productSearch, setProductSearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedProductSearch(productSearch), 500);
    return () => clearTimeout(handler);
  }, [productSearch]);

  const { data: allProductsData, isLoading: loadingAllProducts } = useGetProductsQuery({ search: debouncedProductSearch, limit: 20 });

  useEffect(() => {
    if (!scanOpen) setScanError('');
  }, [scanOpen]);

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku || '',
        name: product.name || '',
        description: product.description || '',
        barcodes: product ? [product.barcode, ...(product.additionalBarcodes || []).map(b => b.barcode)].filter(Boolean) : [],
        categoryId: product.categoryId || '',
        brand: product.brand || '',
        baseUnit: product.baseUnit || 'pcs',
        costPrice: product.costPrice != null && product.costPrice !== '' && Number(product.costPrice) !== 0 ? String(product.costPrice) : '',
        wholesalePrice: product.wholesalePrice != null && product.wholesalePrice !== '' && Number(product.wholesalePrice) !== 0 ? String(product.wholesalePrice) : '',
        sellingPrice: product.sellingPrice != null && product.sellingPrice !== '' && Number(product.sellingPrice) !== 0 ? String(product.sellingPrice) : '',
        openingStock: product.openingStock != null && product.openingStock !== '' && Number(product.openingStock) !== 0 ? String(product.openingStock) : '',
        maxDiscountType: product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount' ? product.maxDiscountType : '',
        maxDiscountPercent: product.maxDiscountPercent != null ? String(product.maxDiscountPercent) : '',
        maxDiscountAmount: product.maxDiscountAmount != null ? String(product.maxDiscountAmount) : '',
        isActive: product.isActive !== undefined ? product.isActive : true,
        type: product.type || 'product',
        hasWarranty: product.hasWarranty || false,
        warrantyPeriodId: product.warrantyPeriodId || '',
        requiresImei: product.requiresImei || false,
        requiresDualImei: product.requiresDualImei || false,
        isBundle: product.isBundle || false,
      });
      if (product.isBundle && product.bundleItems) {
        setBundleItems(product.bundleItems.map(b => ({
          productId: b.productId,
          product: b.product,
          quantity: b.quantity
        })));
      }
      // Set image preview if product has an image
      if (product.imageUrl) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const baseUrl = apiUrl.replace('/api', '');
        setImagePreview(`${baseUrl}${product.imageUrl}`);
      } else {
        setImagePreview(null);
      }
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.sku || !formData.sku.trim()) {
      alert('SKU is required');
      return;
    }
    if (!formData.name || !formData.name.trim()) {
      alert('Product name is required');
      return;
    }

    const cost = parseFloat(formData.costPrice) || 0;
    const wholesale = parseFloat(formData.wholesalePrice) || 0;
    const selling = parseFloat(formData.sellingPrice) || 0;
    if (showWholesalePrice && wholesale < cost) {
      alert('Wholesale price cannot be less than cost price.');
      return;
    }
    if (selling < cost) {
      alert('Selling price cannot be less than cost price.');
      return;
    }

    try {
      // Create FormData if image is being uploaded, otherwise use regular object
      let submitData;
      
      if (imageFile) {
        // Use FormData when uploading an image
        submitData = new FormData();
        submitData.append('sku', formData.sku.trim());
        submitData.append('name', formData.name.trim());
        submitData.append('description', formData.description || '');
        submitData.append('barcodes', JSON.stringify(formData.barcodes || []));
        submitData.append('categoryId', formData.categoryId ? formData.categoryId : '');
        submitData.append('brand', formData.brand || '');
        submitData.append('baseUnit', formData.baseUnit || 'pcs');
        submitData.append('costPrice', parseFloat(formData.costPrice) || 0);
        submitData.append('wholesalePrice', showWholesalePrice ? (parseFloat(formData.wholesalePrice) || 0) : (parseFloat(formData.sellingPrice) || 0));
        submitData.append('sellingPrice', parseFloat(formData.sellingPrice) || 0);
        if (formData.openingStock !== '' && formData.openingStock != null) {
          submitData.append('openingStock', parseFloat(formData.openingStock) || 0);
        }
        if (formData.maxDiscountType === 'percent' || formData.maxDiscountType === 'amount') {
          submitData.append('maxDiscountType', formData.maxDiscountType);
          if (formData.maxDiscountType === 'percent' && formData.maxDiscountPercent !== '') {
            submitData.append('maxDiscountPercent', formData.maxDiscountPercent);
          }
          if (formData.maxDiscountType === 'amount' && formData.maxDiscountAmount !== '') {
            submitData.append('maxDiscountAmount', formData.maxDiscountAmount);
          }
        }
        submitData.append('isActive', formData.isActive);
        submitData.append('type', formData.type || 'product');
        submitData.append('hasWarranty', formData.hasWarranty);
        if (formData.hasWarranty && formData.warrantyPeriodId) {
          submitData.append('warrantyPeriodId', formData.warrantyPeriodId);
        }
        submitData.append('requiresImei', formData.requiresImei);
        submitData.append('requiresDualImei', formData.requiresDualImei);
        submitData.append('isBundle', formData.isBundle);
        if (formData.isBundle && bundleItems.length > 0) {
          submitData.append('bundleItems', JSON.stringify(bundleItems.map(b => ({
            productId: b.productId,
            quantity: b.quantity
          }))));
        }
        submitData.append('image', imageFile);
      } else {
        // Use regular JSON object when no image
        submitData = {
          sku: formData.sku.trim(),
          name: formData.name.trim(),
          description: formData.description || '',
          barcodes: formData.barcodes || [],
          categoryId: formData.categoryId || null,
          brand: formData.brand || '',
          baseUnit: formData.baseUnit || 'pcs',
          costPrice: parseFloat(formData.costPrice) || 0,
          wholesalePrice: showWholesalePrice ? (parseFloat(formData.wholesalePrice) || 0) : (parseFloat(formData.sellingPrice) || 0),
          sellingPrice: parseFloat(formData.sellingPrice) || 0,
          openingStock: formData.openingStock !== '' && formData.openingStock != null ? parseFloat(formData.openingStock) : undefined,
          maxDiscountType: formData.maxDiscountType === 'percent' || formData.maxDiscountType === 'amount' ? formData.maxDiscountType : undefined,
          maxDiscountPercent: formData.maxDiscountType === 'percent' && formData.maxDiscountPercent !== '' ? formData.maxDiscountPercent : undefined,
          maxDiscountAmount: formData.maxDiscountType === 'amount' && formData.maxDiscountAmount !== '' ? formData.maxDiscountAmount : undefined,
          isActive: formData.isActive,
          type: formData.type || 'product',
          hasWarranty: formData.hasWarranty,
          warrantyPeriodId: formData.hasWarranty ? formData.warrantyPeriodId || null : null,
          requiresImei: formData.requiresImei,
          requiresDualImei: formData.requiresDualImei,
          isBundle: formData.isBundle,
          bundleItems: formData.isBundle ? bundleItems.map(b => ({
            productId: b.productId,
            quantity: b.quantity
          })) : [],
        };
      }

      if (isEdit) {
        const result = await updateProduct(
          imageFile ? { id: productId, body: submitData } : { id: productId, ...submitData }
        ).unwrap();
        if (onSuccess) onSuccess(result);
      } else {
        const result = await createProduct(submitData).unwrap();
        if (onSuccess) onSuccess(result);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error?.data?.message || 'Error saving product');
    }
  };

  if (loadingProduct) return <Typography>Loading...</Typography>;

  return (
    <Box>
      {!compact && (
        <Typography variant="h4" component="h1" gutterBottom>
          {isEdit ? 'Edit Product' : 'New Product'}
        </Typography>
      )}

      <Paper sx={{ p: compact ? 0 : 3, elevation: compact ? 0 : 1 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Product Image
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {imagePreview && (
                    <Avatar
                      src={imagePreview}
                      alt="Product preview"
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  )}
                  <Button variant="outlined" component="label">
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {imagePreview && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.barcodes || []}
                onChange={(e, v) => setFormData((prev) => ({ ...prev, barcodes: v }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Barcodes"
                    name="barcodes"
                    helperText="Type a barcode and hit Enter. Leave empty to auto-generate."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Scan barcode"
                              edge="end"
                              onClick={() => setScanOpen(true)}
                            >
                              <QrCodeScannerIcon />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={
                  categoriesData
                    ? [
                        ...categoriesData.filter((c) => c.parentId !== null),
                        ...categoriesData.filter((c) => c.parentId === null),
                      ]
                    : []
                }
                getOptionLabel={(option) =>
                  option.parent?.name
                    ? `${option.parent.name} > ${option.name}`
                    : `${option.name} (Main Category)`
                }
                value={
                  categoriesData?.find((c) => c.id === formData.categoryId) || null
                }
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: newValue ? newValue.id : '',
                  }));
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setCategorySearch(newInputValue);
                  } else if (reason === 'clear') {
                    setCategorySearch('');
                  }
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Category" 
                    helperText="Search and select a category"
                  />
                )}
              />
            </Grid>
            {enableServiceModule && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Item Type"
                  name="type"
                  value={formData.type || 'product'}
                  onChange={handleChange}
                  helperText="Specify whether this is a physical product or a service"
                >
                  <MenuItem value="product">Physical Product</MenuItem>
                  <MenuItem value="service">Service Offering</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Base Unit"
                name="baseUnit"
                value={formData.baseUnit}
                onChange={handleChange}
                helperText="Default unit of measurement for this product"
              >
                <MenuItem value="pcs">Pieces (pcs)</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="pack">Pack</MenuItem>
                <MenuItem value="kg">Kilogram (kg)</MenuItem>
                <MenuItem value="g">Gram (g)</MenuItem>
                <MenuItem value="litre">Litre (L)</MenuItem>
                <MenuItem value="ml">Millilitre (mL)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Cost Price (${currency})`}
                name="costPrice"
                type="text"
                inputMode="decimal"
                placeholder={`Cost Price (${currency})`}
                value={formData.costPrice === 0 || formData.costPrice === '0' ? '' : formData.costPrice}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
              />
            </Grid>
            {showWholesalePrice && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`Wholesale Price (${currency})`}
                  name="wholesalePrice"
                  type="text"
                  inputMode="decimal"
                  placeholder={`Wholesale Price (${currency})`}
                  value={formData.wholesalePrice === 0 || formData.wholesalePrice === '0' ? '' : formData.wholesalePrice}
                  onChange={handleChange}
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                  helperText="Cannot be less than cost price"
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Selling Price (${currency})`}
                name="sellingPrice"
                type="text"
                inputMode="decimal"
                placeholder={`Selling Price (${currency})`}
                value={formData.sellingPrice === 0 || formData.sellingPrice === '0' ? '' : formData.sellingPrice}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                helperText="Cannot be less than cost price"
              />
            </Grid>
            {!isEdit && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock"
                  name="openingStock"
                  type="text"
                  inputMode="decimal"
                  value={formData.openingStock === 0 || formData.openingStock === '0' ? '' : formData.openingStock}
                  onChange={handleChange}
                  placeholder="Opening Stock"
                  helperText="Optional. Sets initial stock at Main Warehouse when creating the product."
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                />
              </Grid>
            )}
            {isEdit && formData.openingStock !== '' && formData.openingStock !== '0' && Number(formData.openingStock) !== 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock (reference)"
                  name="openingStock"
                  value={formData.openingStock}
                  InputProps={{ readOnly: true }}
                  helperText="Opening stock is set at creation; edit stock via Inventory."
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Max discount (sales) – product override
              </Typography>
              <RadioGroup
                row
                value={formData.maxDiscountType || 'none'}
                onChange={(e) => setFormData((prev) => ({ ...prev, maxDiscountType: e.target.value === 'none' ? '' : e.target.value }))}
              >
                <FormControlLabel value="none" control={<Radio />} label="Use company default" />
                <FormControlLabel value="percent" control={<Radio />} label="Percentage" />
                <FormControlLabel value="amount" control={<Radio />} label="Amount per line" />
              </RadioGroup>
              {formData.maxDiscountType === 'percent' && (
                <TextField
                  fullWidth
                  label="Max discount %"
                  name="maxDiscountPercent"
                  type="number"
                  value={formData.maxDiscountPercent}
                  onChange={handleChange}
                  placeholder="e.g. 15"
                  helperText="Product-specific max discount %."
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ maxWidth: 400, mt: 1 }}
                />
              )}
              {formData.maxDiscountType === 'amount' && (
                <TextField
                  fullWidth
                  label={`Max discount amount (${currency})`}
                  name="maxDiscountAmount"
                  type="number"
                  value={formData.maxDiscountAmount}
                  onChange={handleChange}
                  placeholder="e.g. 5"
                  helperText="Product-specific max discount amount per line."
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ maxWidth: 400, mt: 1 }}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasWarranty}
                    onChange={handleChange}
                    name="hasWarranty"
                    color="primary"
                  />
                }
                label="Enable Warranty Tracking"
              />
              {formData.hasWarranty && (
                <TextField
                  fullWidth
                  select
                  label="Warranty Period"
                  name="warrantyPeriodId"
                  value={formData.warrantyPeriodId}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                >
                  <MenuItem value="">Select a period</MenuItem>
                  {warrantyPeriodsData?.filter(w => w.isActive)?.map((wp) => (
                    <MenuItem key={wp.id} value={wp.id}>
                      {wp.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresImei}
                    onChange={handleChange}
                    name="requiresImei"
                    color="primary"
                  />
                }
                label="Requires IMEI / Serial Tracking"
              />
              <Grid size={{xs:12, sm:6}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requiresDualImei}
                      onChange={handleChange}
                      name="requiresDualImei"
                      disabled={!formData.requiresImei}
                    />
                  }
                  label="Requires Dual IMEI (e.g. Dual SIM)"
                />
              </Grid>
              
              <Grid size={{xs:12}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isBundle}
                      onChange={handleChange}
                      name="isBundle"
                    />
                  }
                  label="This Product is a Bundle/Combo Offer"
                />
              </Grid>

              {formData.isBundle && (
                <Grid size={{xs:12}}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" gutterBottom>Bundle Builder</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Autocomplete
                        options={allProductsData?.products?.filter(p => p.id !== productId && !p.isBundle) || []}
                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                        loading={loadingAllProducts}
                        onInputChange={(e, val) => setProductSearch(val)}
                        onChange={(e, newValue) => {
                          if (newValue && !bundleItems.find(b => b.productId === newValue.id)) {
                            setBundleItems(prev => [...prev, { productId: newValue.id, product: newValue, quantity: 1 }]);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Search & Add Product to Bundle" size="small" />
                        )}
                      />
                    </Box>
                    {bundleItems.map((item, index) => (
                      <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography sx={{ flexGrow: 1 }}>{item.product?.name}</Typography>
                        <TextField
                          size="small"
                          label="Qty"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const newItems = [...bundleItems];
                            newItems[index].quantity = parseInt(e.target.value) || 1;
                            setBundleItems(newItems);
                          }}
                          sx={{ width: 80 }}
                        />
                        <Button color="error" onClick={() => {
                          setBundleItems(prev => prev.filter(b => b.productId !== item.productId));
                        }}>
                          Remove
                        </Button>
                      </Box>
                    ))}
                    {bundleItems.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No items in this bundle yet.</Typography>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained">
                {isEdit ? 'Update' : 'Create'}
              </Button>
              {onCancel && (
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Paper>

      <Dialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Scan Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode.
            </Alert>
          )}
          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text) : '';
                if (text) {
                  setFormData((prev) => {
                    const existing = prev.barcodes || [];
                    if (!existing.includes(text)) {
                      return { ...prev, barcodes: [...existing, text] };
                    }
                    return prev;
                  });
                  setScanOpen(false);
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setScanOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductFormContent;
